// Smart Sash Admin Panel - UI State Store

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  title?: string;
  duration?: number;
}

interface Modal {
  id: string;
  component: string;
  props?: Record<string, unknown>;
}

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Theme
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;

  // Notifications
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Modals
  modals: Modal[];
  openModal: (modal: Omit<Modal, 'id'>) => void;
  closeModal: (id: string) => void;
  closeAllModals: () => void;

  // Loading overlay
  isGlobalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;

  // Page title
  pageTitle: string;
  setPageTitle: (title: string) => void;

  // Breadcrumbs
  breadcrumbs: Array<{ label: string; path?: string }>;
  setBreadcrumbs: (breadcrumbs: Array<{ label: string; path?: string }>) => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial State - Sidebar
        sidebarCollapsed: false,
        toggleSidebar: () => {
          set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
        },
        setSidebarCollapsed: (collapsed: boolean) => {
          set({ sidebarCollapsed: collapsed });
        },

        // Initial State - Theme
        theme: 'light',
        setTheme: (theme: 'light' | 'dark') => {
          set({ theme });
          // Apply theme to document
          document.documentElement.setAttribute('data-theme', theme);
        },
        toggleTheme: () => {
          const newTheme = get().theme === 'light' ? 'dark' : 'light';
          get().setTheme(newTheme);
        },

        // Initial State - Notifications
        notifications: [],
        addNotification: (notification) => {
          const id = `notification-${Date.now()}-${Math.random()}`;
          const newNotification: Notification = {
            id,
            duration: 5000, // Default 5 seconds
            ...notification,
          };
          set((state) => ({
            notifications: [...state.notifications, newNotification],
          }));

          // Auto-remove after duration
          if (newNotification.duration && newNotification.duration > 0) {
            setTimeout(() => {
              get().removeNotification(id);
            }, newNotification.duration);
          }
        },
        removeNotification: (id: string) => {
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          }));
        },
        clearNotifications: () => {
          set({ notifications: [] });
        },

        // Initial State - Modals
        modals: [],
        openModal: (modal) => {
          const id = `modal-${Date.now()}-${Math.random()}`;
          const newModal: Modal = { id, ...modal };
          set((state) => ({
            modals: [...state.modals, newModal],
          }));
        },
        closeModal: (id: string) => {
          set((state) => ({
            modals: state.modals.filter((m) => m.id !== id),
          }));
        },
        closeAllModals: () => {
          set({ modals: [] });
        },

        // Initial State - Global Loading
        isGlobalLoading: false,
        setGlobalLoading: (loading: boolean) => {
          set({ isGlobalLoading: loading });
        },

        // Initial State - Page Title
        pageTitle: 'SmartSash Portal',
        setPageTitle: (title: string) => {
          set({ pageTitle: title });
          // Update document title
          document.title = `${title} - SmartSash Portal`;
        },

        // Initial State - Breadcrumbs
        breadcrumbs: [],
        setBreadcrumbs: (breadcrumbs) => {
          set({ breadcrumbs });
        },
      }),
      {
        name: 'ui-storage',
        // Only persist theme and sidebar state
        partialize: (state) => ({
          theme: state.theme,
          sidebarCollapsed: state.sidebarCollapsed,
        }),
      }
    ),
    { name: 'UIStore' }
  )
);
